import React, { useEffect, useState } from "react";

import {
  Check,
  CirclePlus,
  Copy,
  ExternalLink,
  Loader2,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";

import { GuarantorDetails } from "js/components/Tenants/TenantReportDetails/typing";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import { useUpdateGuarantor } from "../../../../hooks/useGuarantors";
import { useAddGuarantorInvestigation } from "../../../../hooks/useTenantReports";
import { toast } from "../../../../utils/toast";
import PersonEditModal from "./PersonEditModal";

interface Props {
  guarantor: GuarantorDetails;
  folio?: string;
  reportId?: string;
  reportStatus?: string;
  guarantorRequired?: boolean;
  guarantorInvestigationRequired?: boolean;
  guarantorUpgradeStatus?: "Pending Payment" | "Paid" | null;
  paymentUrl?: string | null;
  onGuarantorUpdate?: (g: GuarantorDetails) => void;
}

/**
 * VIF (credit investigation) upsell for the PRINCIPAL guarantor. Extracted from the old
 * GuarantorDetailsComponent so it can live next to the guarantor row in the redesigned
 * "Personas del reporte" section. Renders nothing when there's no upsell to show.
 */
const GuarantorInvestigationUpsell: React.FC<Props> = ({
  guarantor,
  folio,
  reportId,
  reportStatus,
  guarantorRequired,
  guarantorInvestigationRequired,
  guarantorUpgradeStatus,
  paymentUrl,
  onGuarantorUpdate,
}) => {
  const [isLegalPerson, setIsLegalPerson] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingInvestigation, setPendingInvestigation] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const updateGuarantorMutation = useUpdateGuarantor();
  const addGuarantorInvestigationMutation = useAddGuarantorInvestigation();

  useEffect(() => {
    setIsLegalPerson(guarantor.legal_type == 2);
  }, [guarantor]);

  const showAddInvestigationButton =
    guarantorRequired === true &&
    guarantorInvestigationRequired === false &&
    guarantorUpgradeStatus == null &&
    !!reportId &&
    (reportStatus === "In Progress" || reportStatus === "in_progress");

  const showPendingPayment = guarantorUpgradeStatus === "Pending Payment";

  if (!showAddInvestigationButton && !showPendingPayment) return null;

  const price = isLegalPerson ? "$499" : "$299";

  const isGuarantorDataComplete = (g: GuarantorDetails) =>
    !!g.name && !!g.first_surname && !!g.email && g.phone_number != null;

  const handleCopyPaymentLink = () => {
    if (paymentUrl) {
      navigator.clipboard
        .writeText(paymentUrl)
        .then(() => toast.success("Enlace de pago copiado"))
        .catch(() => toast.error("No se pudo copiar el enlace"));
    }
  };

  const handleConfirmPayment = () => {
    addGuarantorInvestigationMutation.mutate(reportId!, {
      onSuccess: (data) => {
        const url: string = data.payment_url;
        if (url) window.location.href = url;
      },
    });
  };

  const handleAddInvestigation = () => {
    if (!isGuarantorDataComplete(guarantor)) {
      toast.info(
        "Completa los datos del fiador antes de agregar la investigación.",
      );
      setPendingInvestigation(true);
      setIsModalVisible(true);
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPendingInvestigation(false);
  };

  const handleOk = (values: any) => {
    const updatedGuarantorData = {
      ...guarantor,
      name: values.name,
      first_surname: values.first_surname,
      second_surname: values.second_surname,
      email: values.email,
      phone_number: values.phone_number,
      dial_code: values.dial_code,
      rfc: values.rfc,
      curp: values.curp,
      birth_date: values.birth_date,
      legal_type: isLegalPerson ? 2 : 1,
    } as GuarantorDetails;

    const queryParams = folio ? { folio } : undefined;

    updateGuarantorMutation.mutate(
      {
        guarantor: {
          ...updatedGuarantorData,
          legal_type: isLegalPerson ? 2 : 1,
          phone_number: String(updatedGuarantorData.phone_number),
          dial_code: updatedGuarantorData.dial_code ?? "",
        },
        guarantorId: guarantor?.id ?? "",
        queryParams,
      },
      {
        onSuccess: () => {
          onGuarantorUpdate?.(updatedGuarantorData);
          setIsModalVisible(false);
          if (
            pendingInvestigation &&
            isGuarantorDataComplete(updatedGuarantorData)
          ) {
            setPendingInvestigation(false);
            setTimeout(() => setConfirmDialogOpen(true), 300);
          }
        },
      },
    );
  };

  return (
    <>
      {/* Upsell: 7a — offer card with price anchor */}
      {showAddInvestigationButton && (
        <div className="mt-3 overflow-hidden rounded-xl border border-blue-200">
          <div className="flex items-center gap-2 border-b border-blue-200 bg-blue-50/70 px-3.5 py-2.5">
            <ShieldCheck className="size-4 shrink-0 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">
              Verificación integral del fiador
            </span>
          </div>
          <div className="flex flex-wrap gap-4 p-4">
            <div className="min-w-[240px] flex-1">
              <p className="mb-2.5 text-xs text-muted-foreground">
                Suma el análisis legal y crediticio del fiador a la
                investigación del inquilino.
              </p>
              <div className="flex flex-col gap-2">
                {[
                  "Antecedentes legales a nivel nacional",
                  "Historial en Buró de Crédito",
                  "Listas negras (SAT, FGR, etc.)",
                ].map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-2 text-[13px] text-slate-700"
                  >
                    <Check
                      className="size-3.5 shrink-0 text-blue-600"
                      strokeWidth={2.6}
                    />
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex w-full flex-col gap-1 rounded-xl border border-border bg-muted/40 p-3.5 sm:w-52">
              <span className="text-xs text-muted-foreground">Desde</span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold leading-none text-foreground">
                  {price}
                </span>
                <span className="text-xs text-muted-foreground">IVA incl.</span>
              </div>
              <Button
                size="sm"
                onClick={handleAddInvestigation}
                disabled={addGuarantorInvestigationMutation.isPending}
                className="mt-1 w-full"
              >
                {addGuarantorInvestigationMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <CirclePlus className="size-3.5" />
                )}
                Agregar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pending payment: compact inline banner */}
      {showPendingPayment && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-yellow-200 bg-yellow-50/60 px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <TriangleAlert className="size-4 shrink-0 text-yellow-600" />
            <span className="truncate text-sm font-medium text-yellow-900">
              Investigación del fiador
            </span>
            <Badge className="shrink-0 border-yellow-300 bg-yellow-100 text-[10px] text-yellow-700">
              Pago Pendiente
            </Badge>
          </div>
          {paymentUrl && (
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="default"
                className="h-7 text-xs"
                onClick={() => {
                  window.open(paymentUrl, "_blank", "noopener,noreferrer");
                }}
              >
                <ExternalLink className="size-3" />
                Pagar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={handleCopyPaymentLink}
              >
                <Copy className="size-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      <PersonEditModal
        isVisible={isModalVisible}
        onCancel={handleCancel}
        onOk={handleOk}
        personData={guarantor}
        personType="guarantor"
        loading={updateGuarantorMutation.isPending}
      />

      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Verificación Integral del Fiador</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">
                  Estás por agregar la verificación integral del fiador.
                </p>
                <div className="mb-3 flex items-center justify-between rounded-lg bg-muted px-3.5 py-2.5">
                  <span className="text-sm font-semibold text-foreground">
                    {isLegalPerson ? "Persona moral" : "Persona física"}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {price}{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      IVA incluido
                    </span>
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Al continuar serás redirigido a la página de pago.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPayment}>
              Continuar al pago
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GuarantorInvestigationUpsell;
